/**
 * NORA PRO — Australian Business Number (ABN) validation.
 *
 * Progressive enhancement for the Shopify contact forms that carry an ABN
 * field. The trade-account and quote forms live in Shopify page content, not
 * in this theme, so this script attaches to whatever it finds rather than
 * owning the markup.
 *
 * Behaviour:
 *   - Only touches forms that actually contain input[name="contact[ABN]"].
 *     The ordinary Contact form has no ABN field, so it is never affected.
 *   - On /pages/trade-account the ABN is required. Elsewhere (the quote form)
 *     it stays optional, but is still checksum-validated when filled in, so a
 *     prospect without an ABN is not blocked from asking for a quote.
 *   - Spaces are permitted while typing and the value is normalised to 11
 *     digits on blur and before submit, which is what Shopify stores.
 *   - Validation is the real ABN modulus-89 checksum, not a length check.
 *   - Submission is only blocked when the ABN is genuinely invalid. Everything
 *     else about the form — Shopify's server-side handling, its spam
 *     protection and its hidden fields — is left untouched.
 *   - With JavaScript unavailable the form still submits to Shopify exactly as
 *     it does today. This is a usability layer, not a server-side control.
 *
 * Accessibility: the field is marked aria-invalid, described by the error via
 * aria-describedby, the error node is role="alert" so it is announced, and
 * focus moves to the field so keyboard users land on the problem.
 */
(function () {
  'use strict';

  var ABN_FIELD = 'input[name="contact[ABN]"]';
  var WEIGHTS = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  var TRADE_ACCOUNT_PATH = '/pages/trade-account';

  var MESSAGES = {
    required: 'Enter your ABN. A trade account is for Australian businesses, so we need a valid 11-digit ABN.',
    invalid: 'That ABN does not look right. Check the 11 digits on your invoice or search abr.business.gov.au.',
  };

  /** Strip everything that is not a digit. */
  function digitsOnly(value) {
    return String(value == null ? '' : value).replace(/\D+/g, '');
  }

  /**
   * Australian Business Number checksum (ATO modulus 89).
   * Subtract 1 from the leading digit, apply the positional weights, and the
   * weighted sum of a valid ABN is divisible by 89.
   */
  function isValidAbn(raw) {
    var digits = digitsOnly(raw);
    if (digits.length !== 11) return false;

    var total = 0;
    for (var i = 0; i < 11; i++) {
      var digit = digits.charCodeAt(i) - 48;
      if (i === 0) digit -= 1;
      total += digit * WEIGHTS[i];
    }
    return total % 89 === 0;
  }

  /** Format 11 digits the way the ABR prints them: 51 824 753 556. */
  function formatAbn(digits) {
    if (digits.length !== 11) return digits;
    return (
      digits.slice(0, 2) + ' ' + digits.slice(2, 5) + ' ' + digits.slice(5, 8) + ' ' + digits.slice(8, 11)
    );
  }

  function errorNodeFor(input) {
    var id = (input.id || 'ContactABN') + '-nora-error';
    var node = document.getElementById(id);
    if (node) return node;

    node = document.createElement('p');
    node.id = id;
    node.className = 'form__message nora-abn-error';
    node.setAttribute('role', 'alert');
    node.hidden = true;
    // Sit the message directly after the field so it reads in source order.
    if (input.parentNode) input.parentNode.insertBefore(node, input.nextSibling);
    return node;
  }

  function showError(input, message) {
    var node = errorNodeFor(input);
    node.textContent = message;
    node.hidden = false;
    input.setAttribute('aria-invalid', 'true');

    // Preserve any aria-describedby the page author already set.
    var described = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
    if (described.indexOf(node.id) === -1) {
      described.push(node.id);
      input.setAttribute('aria-describedby', described.join(' '));
    }
  }

  function clearError(input) {
    var node = document.getElementById((input.id || 'ContactABN') + '-nora-error');
    if (node) {
      node.hidden = true;
      node.textContent = '';
    }
    input.removeAttribute('aria-invalid');
  }

  /**
   * @returns {string|null} an error message, or null when the value is acceptable.
   */
  function validate(input, isRequired) {
    var digits = digitsOnly(input.value);
    if (digits.length === 0) return isRequired ? MESSAGES.required : null;
    return isValidAbn(digits) ? null : MESSAGES.invalid;
  }

  function enhance(input) {
    if (input.dataset.noraAbnBound === 'true') return;
    input.dataset.noraAbnBound = 'true';

    var form = input.form;
    var isRequired =
      window.location.pathname.indexOf(TRADE_ACCOUNT_PATH) !== -1 || input.hasAttribute('required');

    // Help browsers and password managers, and give mobile a numeric keypad.
    input.setAttribute('inputmode', 'numeric');
    input.setAttribute('autocomplete', 'off');
    if (isRequired) input.setAttribute('required', 'required');
    if (!input.getAttribute('placeholder')) input.setAttribute('placeholder', '51 824 753 556');

    // Normalise on blur so what the user sees matches what gets submitted.
    input.addEventListener('blur', function () {
      var digits = digitsOnly(input.value);
      if (digits.length === 11) input.value = formatAbn(digits);
      var message = validate(input, isRequired);
      if (message) showError(input, message);
      else clearError(input);
    });

    // Clear the error as soon as the user starts correcting it.
    input.addEventListener('input', function () {
      if (input.getAttribute('aria-invalid') === 'true') clearError(input);
    });

    if (!form) return;

    form.addEventListener('submit', function (event) {
      var message = validate(input, isRequired);
      if (!message) {
        // Submit the normalised 11 digits, which is what we want stored.
        var digits = digitsOnly(input.value);
        if (digits.length === 11) input.value = digits;
        clearError(input);
        return;
      }

      event.preventDefault();
      showError(input, message);
      input.focus();
    });
  }

  function init() {
    var inputs = document.querySelectorAll(ABN_FIELD);
    for (var i = 0; i < inputs.length; i++) enhance(inputs[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
